import type { ActionReturn } from "svelte/action"

/**
 * Retrieves the element parent's size
 * @param callback - Callback function to be called when the size changes
 */
export const parentSize = <T extends Element>(
  element: T,
  callback: (size: { width: number; height: number }) => void,
): ActionReturn<typeof callback> => {
  let cb = callback

  const handleResize = (element: Element) => {
    const { parentElement } = element

    if (parentElement) cb({ width: parentElement.clientWidth, height: parentElement.clientHeight })
  }

  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === element) handleResize(entry.target)
    }
  })

  handleResize(element)
  observer.observe(element)

  return {
    update: (callback) => {
      if (callback === cb) return

      cb = callback
      handleResize(element)
    },
    destroy: () => observer.disconnect(),
  }
}

type MutationCallback<T extends Element> = (el: T, mutations: MutationRecord[]) => void

const getMutationParams = <T extends Element>(
  params: MutationCallback<T> | [MutationCallback<T>, MutationObserverInit],
): [MutationCallback<T>, MutationObserverInit] => (Array.isArray(params) ? params : [params, {}])

export const mutation = <T extends Element>(
  element: T,
  params: MutationCallback<T> | [MutationCallback<T>, MutationObserverInit],
): ActionReturn<typeof params> => {
  let [callback, observerParams] = getMutationParams(params)
  let observer: MutationObserver

  const start = () => {
    observer = new MutationObserver((mutations) => callback(element, mutations))
    observer.observe(element, observerParams)
  }

  start()

  return {
    update: (params) => {
      const [nextCallback, nextObserverParams] = getMutationParams(params)

      if (nextCallback !== callback) (callback = nextCallback)(element, [])
      if (nextObserverParams === observerParams) return

      observerParams = nextObserverParams
      observer.disconnect()
      start()
    },
    destroy: () => observer.disconnect(),
  }
}

export const resize = <T extends HTMLElement>(
  element: T,
  callback: (el: T, entry?: ResizeObserverEntry) => void,
): ActionReturn<typeof callback> => {
  let cb = callback
  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === element) cb(element, entry)
    }
  })

  cb(element)
  observer.observe(element)

  return {
    update: (callback) => {
      if (callback === cb) return

      cb = callback
      callback(element)
    },
    destroy: () => observer.disconnect(),
  }
}

export const mount = <T extends HTMLElement>(
  el: T,
  cb: (el: T) => void | (() => void),
): ActionReturn<typeof cb> => {
  const unmount = cb(el)

  return { destroy: () => unmount?.() }
}
